import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Shuffle, Upload, RefreshCw, ChevronRight, CheckCircle, AlertTriangle, XCircle, Filter, Download, PlayCircle, SearchX, Loader2, HelpCircle, Landmark } from "lucide-react";

const STATUS_COLOR = {
  ok: "bg-green-100 text-green-800 border-green-200",
  alerta: "bg-yellow-100 text-yellow-800 border-yellow-200",
  critico: "bg-red-100 text-red-800 border-red-200",
  // Fix WH22 (2026-08-31): estado nuevo para las conciliaciones que corrieron
  // sin un solo movimiento bancario. En gris, porque no es un problema de plata.
  sin_datos: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_ICON = {
  ok: CheckCircle,
  alerta: AlertTriangle,
  critico: XCircle,
  sin_datos: HelpCircle,
};

const STATUS_LABEL = { ok: "OK", alerta: "Alerta", critico: "Critico", sin_datos: "Sin extracto" };

const CHANNEL_LABELS = {
  efectivo_bbva: "Efectivo BBVA",
  tarjeta_clip: "Tarjeta CLIP",
  rappi: "Rappi",
  uber_eats: "Uber Eats",
  didi: "DiDi",
  oxxo_pay: "Oxxo Pay",
};

function StatusBadge({ status }) {
  const Icon = STATUS_ICON[status] || AlertTriangle;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLOR[status] || "bg-slate-100 text-slate-600"}`}>
      <Icon className="w-3 h-3" />
      {/* Fix WH22 (2026-08-31): antes cualquier estado desconocido se dibujaba
          como "Critico" por el ternario final. */}
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function OpsConciliacion() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reconciliations, setReconciliations] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("reconciliaciones");
  const [filterChannel, setFilterChannel] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [showRunModal, setShowRunModal] = useState(false);
  const [runForm, setRunForm] = useState({ branch_id: "", channel: "efectivo_bbva", period_start: "", period_end: "" });
  const [running, setRunning] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reconRes, movRes, branchRes] = await Promise.all([
        api.get("/ops/reconciliations"),
        api.get("/ops/bank-movements?limit=50"),
        api.get("/branches"),
      ]);
      setReconciliations(reconRes.data || []);
      setMovements(movRes.data?.items || []);
      setBranches(branchRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredRecons = reconciliations.filter(r => {
    if (filterChannel && r.channel !== filterChannel) return false;
    if (filterBranch && r.branch_id !== filterBranch) return false;
    return true;
  });

  const filteredMovs = movements.filter(m => {
    if (filterChannel && m.channel !== filterChannel) return false;
    if (filterBranch && m.branch_id !== filterBranch) return false;
    return true;
  });

  const handleExport = async () => {
    try {
      const resp = await api.get("/ops/reconciliations/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "conciliaciones.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRunReconciliation = async () => {
    if (!runForm.branch_id) { toast.error("Selecciona una sucursal"); return; }
    if (!runForm.period_start || !runForm.period_end) { toast.error("Completa el rango de fechas"); return; }
    setRunning(true);
    try {
      await api.post("/ops/reconciliations/run", runForm);
      toast.success("Conciliacion ejecutada");
      setShowRunModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ejecutar la conciliacion");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="ops-conciliacion-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shuffle className="w-6 h-6 text-yellow-500" />
            Conciliacion
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Cruce de cortes vs depositos bancarios y de plataformas</p>
        </div>
        <div className="flex gap-2">
          {/* Fix WH75 (2026-10-13): acceso directo a la conciliacion de pagos con el archivo del banco */}
          <button
            onClick={() => navigate("/ops/pagos-banco")}
            data-testid="btn-ver-pagos-banco"
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg font-semibold text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <Landmark className="w-4 h-4" /> Pagos del banco
          </button>
          <button
            onClick={() => navigate("/ops/faltantes")}
            data-testid="btn-ver-faltantes"
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg font-semibold text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <SearchX className="w-4 h-4" /> Faltantes
          </button>
          <button
            onClick={() => setShowRunModal(true)}
            data-testid="btn-ejecutar-conciliacion"
            className="flex items-center gap-2 px-4 py-2 border border-slate-900 rounded-lg font-semibold text-sm text-slate-900 hover:bg-slate-50 transition-colors"
          >
            <PlayCircle className="w-4 h-4" /> Ejecutar conciliacion
          </button>
          <button
            onClick={() => navigate("/ops/conciliacion/import")}
            data-testid="btn-importar-movimientos"
            className="flex items-center gap-2 px-4 py-2 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors"
          >
            <Upload className="w-4 h-4" /> Importar movimientos
          </button>
          <button
            onClick={fetchData}
            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {showRunModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={e => e.target === e.currentTarget && setShowRunModal(false)}
        >
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm space-y-4">
            <h3 className="font-bold text-slate-900">Ejecutar conciliacion</h3>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Sucursal</label>
              <select value={runForm.branch_id} onChange={e => setRunForm(p => ({ ...p, branch_id: e.target.value }))}
                data-testid="run-branch-select"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Canal</label>
              <select value={runForm.channel} onChange={e => setRunForm(p => ({ ...p, channel: e.target.value }))}
                data-testid="run-channel-select"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                {Object.entries(CHANNEL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Desde</label>
                <input type="date" value={runForm.period_start} onChange={e => setRunForm(p => ({ ...p, period_start: e.target.value }))}
                  data-testid="run-period-start" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Hasta</label>
                <input type="date" value={runForm.period_end} onChange={e => setRunForm(p => ({ ...p, period_end: e.target.value }))}
                  data-testid="run-period-end" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowRunModal(false)} className="flex-1 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50 transition-colors">
                Cancelar
              </button>
              <button onClick={handleRunReconciliation} disabled={running} data-testid="confirm-run-btn"
                className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50">
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Ejecutar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="flex gap-3 flex-wrap">
        <select
          value={filterChannel}
          onChange={e => setFilterChannel(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white"
          data-testid="filter-channel"
        >
          <option value="">Todos los canales</option>
          {Object.entries(CHANNEL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select
          value={filterBranch}
          onChange={e => setFilterBranch(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white"
          data-testid="filter-branch"
        >
          <option value="">Todas las sucursales</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {[["reconciliaciones", "Conciliaciones"], ["movimientos", "Movimientos bancarios"]].map(([tab, label]) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab ? "border-[#FFDF00] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : activeTab === "reconciliaciones" ? (
        <div>
          {filteredRecons.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Shuffle className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No hay conciliaciones todavia</p>
              <p className="text-sm mt-1">Importa movimientos bancarios y ejecuta la conciliacion para ver resultados.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRecons.map(r => (
                <div
                  key={r.id}
                  onClick={() => navigate(`/ops/conciliacion/${r.id}`)}
                  className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors"
                  data-testid={`recon-row-${r.id}`}
                >
                  <div className="flex items-center gap-4">
                    <StatusBadge status={r.status} />
                    <div>
                      <div className="font-medium text-slate-900 text-sm">{CHANNEL_LABELS[r.channel] || r.channel}</div>
                      <div className="text-xs text-slate-500">{r.period_start} — {r.period_end}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-right">
                      <div className="text-slate-500 text-xs">Cortes</div>
                      <div className="font-semibold">${(r.cortes_total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-500 text-xs">Depositos</div>
                      <div className="font-semibold">${(r.deposits_total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-500 text-xs">Diferencia</div>
                      <div className={`font-semibold ${r.difference > 0 ? "text-red-600" : "text-green-600"}`}>
                        ${(r.difference || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} ({r.difference_pct || 0}%)
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          {filteredMovs.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Upload className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No hay movimientos importados</p>
              <p className="text-sm mt-1">
                <button onClick={() => navigate("/ops/conciliacion/import")} className="text-yellow-600 hover:underline font-medium">Importa el extracto de BBVA</button> o el reporte de liquidaciones CLIP.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className="pb-2 font-semibold text-slate-600">Fecha</th>
                    <th className="pb-2 font-semibold text-slate-600">Canal</th>
                    <th className="pb-2 font-semibold text-slate-600">Referencia</th>
                    <th className="pb-2 font-semibold text-slate-600 text-right">Monto neto</th>
                    <th className="pb-2 font-semibold text-slate-600 text-right">Comisión</th>
                    <th className="pb-2 font-semibold text-slate-600">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMovs.map(m => (
                    <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-2 text-slate-700">{m.movement_date}</td>
                      <td className="py-2">{CHANNEL_LABELS[m.channel] || m.channel}</td>
                      <td className="py-2 font-mono text-xs text-slate-500">{m.reference}</td>
                      <td className="py-2 text-right font-semibold">${(m.amount_net || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td>
                      <td className="py-2 text-right" data-testid={`movement-commission-${m.id}`}>
                        {m.amounts_pending_settlement ? (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">Pendiente de liquidación</span>
                        ) : m.amount_commission != null ? (
                          <span className="font-semibold">${m.amount_commission.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          m.match_status === "matched" ? "bg-green-100 text-green-700" :
                          m.match_status === "partial" ? "bg-yellow-100 text-yellow-700" :
                          "bg-slate-100 text-slate-600"
                        }`}>
                          {m.match_status === "matched" ? "Conciliado" : m.match_status === "partial" ? "Parcial" : "Sin conciliar"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
